import {
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { ProTable } from "@ant-design/pro-components";
import {
  Avatar,
  Button,
  Input,
  InputRef,
  message,
  Popconfirm,
  Space,
  Switch,
} from "antd";
import type { ActionType, ProColumns } from "@ant-design/pro-components";
import { ReactNode, useRef, useState } from "react";
import lang from "src/lang";
import Export from "src/common/components/Export";
import apiRequest from "src/common/utils/apiRequest";

import { User } from "src/modules/setting/user-management/types";
import coreFunctions from "src/common/utils/coreFunctions";
import { useNavigate } from "react-router-dom";

const UsersTable: React.FC = () => {
  const actionRef = useRef<ActionType>();
  const [allSearch, setAllSearch] = useState<string>("");
  const [pageSize, setPageSize] = useState<number>(10);
  const searchInput = useRef<InputRef>(null);
  const [tableData, setTableData] = useState<User[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const expobj = new Export("users, " + new Date().toDateString());
  const [searchAttributes, setSearchAttributes] = useState<
    { key: string; value: string }[]
  >([]);

  const navigate = useNavigate();

  const handleSearch = (field: string, value: string) => {
    setAllSearch("");
    const existingAttribute = searchAttributes.find(
      (attr) => attr.key === field
    );

    if (existingAttribute) {
      setSearchAttributes(
        searchAttributes.map((attr) =>
          attr.key === field ? { ...attr, value } : attr
        )
      );
    } else {
      setSearchAttributes([
        ...searchAttributes,
        {
          key: field,
          value,
        },
      ]);
    }
  };

  const handleToggleActive = (id: string, checked: boolean) => {
    const API_REQUEST = new apiRequest("user/update/status", true);

    API_REQUEST.send(
      { id, status: checked ? 1 : 0 },
      (response: { status: string; message: string }) => {
        if (response.status === "success") {
          message.success(response.message);
          actionRef.current?.reload();
        } else {
          message.error(response.message);
        }
      }
    );
  };

  const deleteUser = (id: string) => {
    const API_REQUEST = new apiRequest("user/delete", true);

    API_REQUEST.send(
      { id },
      (response: { status: string; message: string }) => {
        if (response.status === "success") {
          message.success(response.message);
          actionRef.current?.reload();
        } else {
          message.error(response.message);
        }
      }
    );
  };

  const searchCols = (field: string, label: string) => {
    return {
      filterDropdown: (props: any) => {
        const { close } = props;

        setTimeout(() => {
          if (searchInput.current) {
            searchInput.current.focus();
          }
        }, 100);

        return (
          <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
            <Input
              ref={searchInput}
              placeholder={lang.get("setting", [`Search ${label}`])}
              onChange={(e) => handleSearch(field, e.target.value)}
              value={
                searchAttributes.filter((item) => item.key === field)[0]?.value
              }
              style={{ marginBottom: 8, display: "block" }}
              onKeyUp={(e) => {
                if (e.key === "Enter") {
                  close();
                  if (actionRef.current) {
                    actionRef.current?.reset?.();
                    actionRef.current.reload();
                  }
                }
              }}
            />
            <Space>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                size="small"
                onClick={() => {
                  actionRef.current?.reset?.();
                  actionRef.current?.reload();
                }}
                style={{ width: 90 }}
              >
                {lang.get("setting", ["Search"])}
              </Button>
              <Button
                onClick={() => close()}
                size="small"
                style={{ width: 90 }}
              >
                {lang.get("setting", ["Close"])}
              </Button>
            </Space>
          </div>
        );
      },
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
      ),
    };
  };

  const columns: ProColumns<User>[] = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      sorter: false,
      render: (fullName: ReactNode, record: User, index: number) => index + 1,
      align: "left",
      width: 50,
    },
    {
      title: "Full Name",
      dataIndex: "name",
      key: "full_name",
      sorter: true,
      render: (_, record) => (
        <Space>
          <Avatar src={record.profile_image} icon={<UserOutlined />} />
          <span>
            {record.first_name +
              (record.last_name ? " " + record.last_name : "")}
          </span>
        </Space>
      ),
      align: "left",
      width: 250,
      ...searchCols("full_name", "Full Name"),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: true,
      render: (email: ReactNode) => <span>{email}</span>,
      align: "left",
      width: 300,
      ...searchCols("email", "Email"),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      sorter: true,
      render: (role: ReactNode) => <span>{role}</span>,
      align: "left",
      width: 200,
      ...searchCols("role", "Role"),
    },
    {
      title: "Last Login",
      dataIndex: "last_login",
      key: "last_login",
      sorter: true,
      render: (last_login: ReactNode) => <span>{last_login}</span>,
      align: "left",
      width: 200,
      ...searchCols("last_login", "Last Login"),
    },
    {
      title: "Active",
      dataIndex: "active",
      key: "active",
      render: (_, record) => (
        <Switch
          checked={record.status}
          onChange={(checked) => handleToggleActive(record.id, checked)}
        />
      ),
      align: "center",
      width: 150,
    },
    {
      title: "Options",
      key: "actions",
      width: 150,
      render: (_, record) => (
        <Space size="middle">
          <Button
            icon={<EditOutlined />}
            onClick={() => navigate(`/modules/setting/user/${record.id}`)}
          />
          <Popconfirm
            title="Are you sure to delete this user?"
            onConfirm={() => {
              deleteUser(record.id);
            }}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <ProTable
        headerTitle={lang.get("setting", ["Users"])}
        columns={columns}
        actionRef={actionRef}
        dataSource={tableData}
        request={async (params, sort, filter) => {
          await new coreFunctions().waitRequest(10);

          const processedFilters = Object.entries(filter || {}).reduce(
            (acc, [key, value]) => {
              if (value && value.length > 0) {
                acc[key] = value;
              }
              return acc;
            },
            {} as Record<string, (string | number)[]>
          );

          const payload = {
            params: params,
            sorts: sort,
            filters: processedFilters,
            searches: searchAttributes,
            all_search: allSearch,
          };

          const API_REQUEST = new apiRequest("users/get", true);
          const response = await API_REQUEST.send(payload);

          if (response && response.status === "success") {
            const fetchedData = response.data || [];
            setTableData(fetchedData);
            setTotalRecords(response.total);

            return {
              data: fetchedData,
              total: totalRecords,
              success: true,
            };
          }

          return {
            data: [],
            total: 0,
            success: false,
          };
        }}
        rowKey="id"
        pagination={{
          defaultCurrent: 1,
          pageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50],
          showTotal: (total, range) =>
            lang.get("setting", [
              `Showing ${range[0]}-${range[1]} of ${total} items`,
            ]),
          onShowSizeChange: (current, size) => {
            setPageSize(size);
          },
        }}
        search={false}
        scroll={{ x: 800 }}
        cardBordered
        toolBarRender={() => [
          <Input
            style={{ width: "auto" }}
            key="all_search"
            allowClear
            placeholder={lang.get("setting", ["Global Search"])}
            value={allSearch}
            onChange={(e) => {
              setAllSearch(e.target.value);
              setSearchAttributes([]);
              actionRef?.current?.reload();
            }}
          />,
          <Button
            key="csv"
            onClick={() => {
              const csvData = tableData.map((row) => ({
                [lang.get("setting", ["Name"])]:
                  row.first_name + " " + row.last_name,
                [lang.get("setting", ["Email"])]: row.email,
                [lang.get("setting", ["Role"])]: row.role,
                [lang.get("setting", ["Last Login"])]: row.last_login,
                [lang.get("setting", ["Active"])]:
                  row.status == true ? "Yes" : "No",
              }));
              expobj.exportToCSV(csvData);
            }}
          >
            {lang.get("setting", ["Export CSV"])}
          </Button>,
          <Button
            key="csv"
            onClick={() => {
              const csvData = tableData.map((row) => ({
                [lang.get("setting", ["Name"])]:
                  row.first_name + (row.last_name ? " " + row.last_name : ""),
                [lang.get("setting", ["Email"])]: row.email,
                [lang.get("setting", ["Role"])]: row.role,
                [lang.get("setting", ["Last Login"])]: row.last_login,
                [lang.get("setting", ["Active"])]:
                  row.status == true ? "Yes" : "No",
              }));
              expobj.exportToCSV(csvData);
            }}
          >
            {lang.get("setting", ["Search"])}
          </Button>,
          <Button
            key="excel"
            onClick={() => {
              const excelData = tableData.map((row) => ({
                [lang.get("setting", ["Name"])]:
                  row.first_name + (row.last_name ? " " + row.last_name : ""),
                [lang.get("setting", ["Email"])]: row.email,
                [lang.get("setting", ["Role"])]: row.role,
                [lang.get("setting", ["Last Login"])]: row.last_login,
                [lang.get("setting", ["Active"])]:
                  row.status == true ? "Yes" : "No",
              }));
              expobj.exportToExcel(excelData);
            }}
          >
            {lang.get("setting", ["Export Excel"])}
          </Button>,

          <Button
            key="pdf"
            onClick={() => {
              expobj.exportToPDF({
                head: [
                  [
                    lang.get("setting", ["Name"]),
                    lang.get("setting", ["Email"]),
                    lang.get("setting", ["Role"]),
                    lang.get("setting", ["Last Login"]),
                    lang.get("setting", ["Active"]),
                  ],
                ],
                body: tableData.map((row) => [
                  row.first_name + (row.last_name ? " " + row.last_name : ""),
                  row.email,
                  row.role,
                  row.last_login,
                  row.status == true ? "Yes" : "No",
                ]),
              });
            }}
          >
            {lang.get("setting", ["Export PDF"])}
          </Button>,
        ]}
      />
    </div>
  );
};

export default UsersTable;
